<template>
  <div id="app" class="shadow">
    <nav class="navbar main-nav navbar-expand-md">
      <a title="Home" href="/" target="_self" class="navbar-brand">
        <img :src="this.logo" class="navbar-logo" alt="AIS Logo" />
      </a>

      <div id="nav-collapse" class="navbar-collapse">
        <ul class="navbar-nav main-nav-items">
          <template v-for="menu in menuInfo">
            <li class="nav-item nav-link" :key="menu.slug" :class="{ 'menu-item-has-children' : menu.hasChildren}">
              <template v-if="menu.parent === null">
                <template v-if="menu.hasChildren">
                  <a aria-current="page" class="nav-link" :class="{'router-link-exact-active router-link-active': activeUrl(menu.slug, menu.children)}">
                    {{ menu.title }}
                  </a>
                </template>

                <template v-if="!menu.hasChildren">
                  <a aria-current="page" :href="menu.slug" class="nav-link" :class="{'router-link-exact-active router-link-active': activeUrl(menu.slug)}">
                    {{ menu.title }}
                  </a>
                </template>

                <ul class="sub-menu" v-if="menu.hasChildren">
                  <template v-for="child in menu.children">
                    <li :key="child.url">
                      <a :href="child.url" target="_self" :class="{'nav-link router-link-exact-active router-link-active': activeUrl(child.url)}">
                        {{ child.title }}
                      </a>
                    </li>
                  </template>
                </ul>
              </template>
            </li>
          </template>
        </ul>

        <ul class="navbar-nav language">
          <template v-for="lang in languageMenu">
            <li class="nav-item" :key="lang.id">
              <template v-if="$route.path.startsWith(lang.slug.substring(0, 3))">
                <a :href="lang.slug" aria-current="page" class="nav-link router-link-exact-active router-link-active" target="_self">
                  <div class="lang-selection">{{ lang.title }}</div>
                </a>
              </template>

              <template v-if="!$route.path.startsWith(lang.slug.substring(0, 3))">
                <a :href="lang.slug" aria-current="page" class="nav-link" target="_self">
                  <div class="lang-selection">{{ lang.title }}</div>
                </a>
              </template>
            </li>
          </template>
        </ul>
      </div>

      <div class="toggler">
        <button type="button"
              id="navbar-toggler"
              aria-label="Toggle navigation"
              class="navbar-toggler toggle-button"
              @click="toggleNav()">
          <font-awesome-icon :icon="bars" />
        </button>
      </div>
    </nav>

    <transition
      name="fade"
      mode="out-in">
      <router-view :key="$route.fullPath" />
    </transition>

    <ais-footer />

    <button v-on:click="scrollToTop" id="scrollUpBtn" title="Go to top">
      <font-awesome-icon :icon="goUp" />
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { multipleLanguages, defaultLanguage, hasGraduates } from './_config/config'
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp'
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars'

export default {
  name: 'App',
  data () {
    return {
      logo: require('@/assets/logo/logo.png'),
      menuInfo: [],
      languageMenu: [],
      goUp: faArrowUp,
      bars: faBars
    }
  },
  computed: {
    ...mapGetters('website', [
      'getWebsite',
      'getCurrentLanguage',
      'getMenuInfo',
      'getAlternativeLinks',
      'getLanguageMenu',
      'getLogo'
    ])
  },
  methods: {
    activeUrl (slug, children = []) {
      if (this.$route.path.length === 3) {
        // Homepage
        if (slug.length !== 4) {
          return false
        } else {
          return (this.$route.path === slug.substring(0, 3))
        }
      }

      if (children.length > 0) {
        for (let c = 0; c < children.length; c++) {
          if (children[c].url === this.$route.path) {
            return true
          }
        }
      }

      return (this.$route.path === slug)
    },
    getLangIsoCode (lang) {
      if (typeof lang === 'undefined' || lang.length === 0) {
        // There is no language given in the URL, let's check the browser language and switch accordingly.
        lang = navigator.language || navigator.userLanguage

        if (lang.indexOf('nl') !== -1) {
          lang = 'nl'
        } else if (lang.indexOf('de') !== -1) {
          lang = 'de'
        } else {
          lang = 'en'
        }

        if (!multipleLanguages) {
          // Report website only has the English language.
          lang = defaultLanguage
        }
      }

      return lang
    },
    setupNav () {
      if (this.getWebsite !== null) {
        const currentPath = this.$route.path
        // this.siteName = this.getWebsite.siteName
        this.logo = this.getLogo
        this.menuInfo = this.getMenuInfo
        this.languageMenu = this.getLanguageMenu(currentPath)
      }
    },
    scrollToTop () {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    },
    toggleNav () {
      document.getElementById('nav-collapse').classList.toggle('responsive')
    }
  },
  watch: {
    $route (to, from) {
      if (to.params.lang !== from.params.lang) {
        this.$store.dispatch('website/changeCurrentLanguage', { isoCode: to.params.lang })
      }

      let toPath = to.fullPath
      toPath = toPath.replace('/', '=')
      toPath = toPath.replace('/reports/', '/reports_~_')
      toPath = toPath.replace('=', '/')

      this.$store.dispatch('page/get', { path: toPath })
      this.setupNav()
    },
    getWebsite (oldWebsite, newWebsite) {
      this.setupNav()
    }
  },
  async created () {
    window.addEventListener('scroll', () => {
      var toTop = document.getElementById('scrollUpBtn')

      if (document.body.scrollTop > 20 || document.documentElement.scrollTop > 20) {
        if (!toTop.classList.contains('active')) {
          toTop.classList.remove('inactive')
          toTop.classList.toggle('active')
        }
      } else {
        if (toTop.classList.contains('active')) {
          toTop.classList.add('inactive')
          toTop.classList.toggle('active')
        }
      }
    })

    document.addEventListener('click', function (event) {
      const ignoreList = document.getElementsByClassName('show')
      const dropdowns = document.getElementsByClassName('menu-item-has-children')

      for (let j = 0; j < ignoreList.length; j++) {
        if (!ignoreList[j].contains(event.target)) {
          ignoreList[j].classList.remove('show')
        }
      }

      for (let k = 0; k < dropdowns.length; k++) {
        if (dropdowns[k].contains(event.target)) {
          const target = dropdowns[k].children[1]
          target.classList.add('show')
        }
      }
    })

    await this.$store.dispatch('website/get', { isoCode: this.getLangIsoCode(this.$route.params.lang) })
    if (hasGraduates) {
      this.$store.dispatch('graduates/all')
    }
  },
  mounted () {
    let currentPath = this.$route.path

    if (this.$route.path === '/') {
      var userLang = this.getLangIsoCode('')
      currentPath = '/' + userLang
      this.$router.push(currentPath)
    }

    currentPath = currentPath.replace('/', '=')
    currentPath = currentPath.replace('/reports/', '/reports_~_')
    currentPath = currentPath.replace('=', '/')

    this.$store.dispatch('page/get', { path: currentPath })
  }
}
</script>

<style>
:root {
  --blue: #007bff;
  --indigo: #6610f2;
  --purple: #6f42c1;
  --pink: #e83e8c;
  --red: #dc3545;
  --orange: #fd7e14;
  --yellow: #ffc107;
  --green: #28a745;
  --teal: #20c997;
  --cyan: #17a2b8;
  --white: #fff;
  --gray: #6c757d;
  --gray-dark: #343a40;
  --primary: #007bff;
  --secondary: #6c757d;
  --success: #28a745;
  --info: #17a2b8;
  --warning: #ffc107;
  --danger: #dc3545;
  --light: #f8f9fa;
  --dark: #343a40;
  --breakpoint-xs: 0;
  --breakpoint-sm: 576px;
  --breakpoint-md: 768px;
  --breakpoint-lg: 992px;
  --breakpoint-xl: 1200px;
  --font-family-sans-serif: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
  --font-family-monospace: SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace;
}

* {
  box-sizing: border-box;
  font-display: swap;
}

html, body {
  background: #ddd;
  position: relative;
  padding: 0px;
  margin: 0px;
  line-height: 1.5;
  font-weight: 400;
}

.cookie__floating {
  max-width: 450px;
  height: 435px;
}

.cookie__floating__content {
  max-height: 400px;
}

hr {
  max-height: 0px;
  display: block;
  border: 0px;
  border-top: 1px solid rgba(0,0,0,.1);
  margin-block-start: 0.5em;
  margin-block-end: 0.5em;
}

#app {
  font-family: Roboto;
  font-size: 15px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  margin-left: auto;
  margin-right: auto;
  margin-top: 0px;
  max-width: 1366px;
  background: #fff;
  scroll-behavior: smooth;
  box-shadow: 8px 0 8px -8px rgba(0, 0, 0, 0.5), -8px 0 8px -8px rgba(0, 0, 0, 0.5);
}

.main-content {
  min-height: 250px;
}

.navbar {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
}

.navbar ul {
  list-style-type: none;
}

.navbar-nav {
  display: flex;
  color: #fff;
  font-size: 14px;
  text-transform: uppercase;
  padding-right: 10px;
  padding-left: 10px;
  flex-direction: row;
  list-style-type: none;
  justify-content: right;
  overflow: hidden;
}

.navbar-nav .main-nav-items {
  flex-basis: auto;
  display: flex;
}

.navbar-brand {
  flex-basis: auto;
  margin-top: 10px;
  padding-left: 1rem;
  padding-right: 1rem;
}

.navbar-collapse {
  display: flex;
  flex-basis: auto;
  flex-grow: 1;
  align-items: center;
}

.main-nav {
  min-height: 80px;
  background: #d0e3f3;
  background: linear-gradient(90deg, #d0e3f3 0%, #262b55 40%, #262b55 100%);
  box-shadow: 0 10px 8px -8px #000;
}

.navbar-nav .nav-item .nav-link {
  padding: 0px;
  margin: 0px;
  word-wrap: break-word;
  color: #fff;
  /* margin-left: 10px; */
  margin-right: 10px;
  color:hsla(0,0%,100%,.5);
  text-decoration: none;
  border: 0px;
}

.toggle-button {
  overflow-anchor: none;
  display: flex;
  background: transparent;
  padding: 10px;
  padding-bottom: 10px;
  font-size: 1.5rem;
  border-radius: .25rem;
  color: hsla(0,0%,100%,.5);
  border-color: hsla(0,0%,100%,.1);
}

.navbar-nav ul.sub-menu {
  display: none;
}

a.nav-link.router-link-exact-active.router-link-active,
a.nav-link.router-link-active,
a.nav-link {
  color:#ce5e31;
}

.navbar-nav .nav-item .nav-link:hover,
.navbar-nav .nav-item .nav-link:focus,
.navbar-nav .nav-item .nav-link:active {
  color: #ce5e31;
}

ul.sub-menu a {
  text-decoration: none;
  color: hsla(0,0%,100%,.5);
}

ul.sub-menu a:hover {
  color: #ce5e31;
}

.lang-selection {
  border: 1px solid #fff;
  border-radius: 100%;
  margin: 0px 5px 0px 0px;
  min-width: 30px;
  max-width: 30px;
  text-align: center;
  font-size: .6rem;
  display: inline-block;
  color: #fff;
}

.language a.nav-link.router-link-exact-active.router-link-active .lang-selection {
  background-color: #fff;
  color: #000;
  border: 1px solid #fff;
  border-radius: 100%;
  margin: 0px 5px 0px 0px;
  min-width: 30px;
  max-width: 30px;
  text-align: center;
  font-size: .6rem;
  display: inline-block;
}

select {
  text-rendering: auto !important;
}

#scrollUpBtn {
  display: block; /* Hidden by default */
  position: fixed; /* Fixed/sticky position */
  bottom: 20px; /* Place the button at the bottom of the page */
  right: 20px; /* Place the button 30px from the right */
  z-index: 99; /* Make sure it does not overlap */
  border: none; /* Remove borders */
  outline: none; /* Remove outline */
  background-color: #5ea9d4; /* Set a background color */
  color: white; /* Text color */
  cursor: pointer; /* Add a mouse pointer on hover */
  padding: 10px; /* Some padding */
  padding-left: 15px;
  padding-right: 15px;
  border-radius: 5px; /* Rounded corners */
  font-size: 18px; /* Increase font size */
  /* transition: opacity 1s ease; */
  opacity: 0;
  height: 0;
  overflow: hidden;
}

#scrollUpBtn.active {
  height: auto;
  opacity: 1;
  animation: scrollUpIn 1s;
}

@keyframes scrollUpIn {
  0% { opacity: 0; height: auto; }
  100% { opacity: 1; height: auto; }
}

#scrollUpBtn.inactive {
  animation: scrollUpOut 2s;
}

@keyframes scrollUpOut {
  0% { opacity: 1; height: auto; }
  50% { opacity: 0; height: auto; }
  100% { height: 0 }
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.5s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.btn-zoom {
  transition: transform .2s; /* Animation */
  box-shadow: 0 10px 8px -8px #000;
  backdrop-filter: saturate(180%) blur(10px);
}

.btn-zoom-noshadow {
  transition: transform .2s; /* Animation */
}

.btn-zoom:hover,
.btn-zoom-noshadow:hover {
  transform: scale(1.2);
}

p {
  font-family: Roboto;
  font-size: 15px;
  color: #000;
  margin-bottom: 1rem;
  margin-block-start: 1em;
  margin-block-end: 1em;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  margin-top: 0;
  margin-bottom: .5rem;
  line-height: 1.2;
}

h1 {
  font-family: Roboto;
  font-size: 24px;
  color: #787878;
  font-weight: 700;
}

h2 {
  font-family: Roboto;
  font-size: 20px;
  color: #787878;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: space-around;
  text-align: center;
}

.btn a {
  text-decoration: none;
}

.navbar-logo {
  max-width: 100%;
  max-height: 60px;
}

video {
  border: 1px solid #000;
  border-radius: 10px;
}

.image-portrait {
  border-radius: 5px;
  width: 32%;
  margin: 5px;
  height: auto;
  max-height: 500px;
  max-width: 32%;
  overflow: hidden;
  object-fit: cover;
  object-position: center;
  transition: 1s ease-in-out;
}

.image-landscape {
  border-radius: 5px;
  width: 32%;
  margin: 5px;
  height: auto;
  max-height: 240px;
  max-width: 32%;
  overflow: hidden;
  object-fit: cover;
  object-position: center;
  transition: 1s ease-in-out;
}

/* Small devices < 568px */
@media only screen and (max-width: 568px) {
  .website-container {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  [class*="col-"] {
    width: 100%
  }

  .toggler {
    position: absolute;
    top: 1rem;
    right: 1rem;
  }

  .navbar-collapse {
    /* background: #262b55; */
    max-height: 0px;
    flex-direction: column;
    width: 100%;
    transition: all 1s ease-in-out;
  }

  .navbar {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .navbar-nav {
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
  }

  .navbar-nav ul {
    width: 100%;
    margin: 0px;
    padding: 0px;
  }

  .navbar-nav li {
    width: 100%;
    text-align: right;
    padding: .5rem 0rem;
  }

  .language {
    width: 100%;
    padding: .5rem 0rem;
  }

  .responsive {
    display: flex;
    height: auto;
    max-height: 100vh;
    transition: all 1s ease-in-out;
  }

  .main-nav {
    background: linear-gradient(90deg, #d0e3f3 0%, #262b55 100%);
  }

  .image-landscape,
  .image-portrait {
    width: 100%;
    max-width: 100%;
  }

  .sub-menu.show {
    display: flex !important;
  }

  ul.sub-menu {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    position: relative;
    padding-right: 20px;
    margin-top: 0px;
    margin-bottom: 0px;
    padding-top: 0px;
    padding-bottom: 0px;
    z-index: 100;
    border-radius: .5rem;
    background-color: #000;
    min-height: 50px;
    max-height: 900px;
    height: auto;
    overflow: visible;
  }
}

/* Small devices >= 568px */
@media only screen and (min-width: 568px) {
  .col-sm-1 {width: 8.33%;}
  .col-sm-2 {width: 16.66%;}
  .col-sm-3 {width: 25%;}
  .col-sm-4 {width: 33.33%;}
  .col-sm-5 {width: 41.66%;}
  .col-sm-6 {width: 50%;}
  .col-sm-7 {width: 58.33%;}
  .col-sm-8 {width: 66.66%;}
  .col-sm-9 {width: 75%;}
  .col-sm-10 {width: 83.33%;}
  .col-sm-11 {width: 91.66%;}
  .col-sm-12 {width: 100%;}

  .website-container {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .toggler {
    position: absolute;
    top: 1rem;
    right: 1rem;
  }

  .navbar-collapse {
    /* background: #262b55; */
    max-height: 0px;
    flex-direction: column;
    width: 100%;
    transition: all 1s ease-in-out;
  }

  .navbar {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .navbar-nav {
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
  }

  .navbar-nav ul {
    width: 100%;
    margin: 0px;
    padding: 0px;
  }

  .navbar-nav li {
    width: 100%;
    text-align: right;
    padding: .5rem 0rem;
  }

  .language {
    width: 100%;
    padding: .5rem 0rem;
  }

  .responsive {
    display: flex;
    height: auto;
    max-height: 100vh;
    transition: all 1s ease-in-out;
  }

  .main-nav {
    background: linear-gradient(90deg, #d0e3f3 0%, #262b55 50%, #262b55 100%);
  }

  .image-landscape,
  .image-portrait {
    width: 100%;
    max-width: 100%;
  }

  .sub-menu.show {
    display: flex !important;
  }

  ul.sub-menu {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    position: relative;
    padding-right: 20px;
    margin-top: 0px;
    margin-bottom: 0px;
    padding-top: 0px;
    padding-bottom: 0px;
    z-index: 100;
    border-radius: .5rem;
    background-color: #000;
    min-height: 50px;
    max-height: 900px;
    height: auto;
    overflow: visible;
  }
}

/* Medium devices >= 768px */
@media only screen and (min-width: 768px) {
  .col-md-1 {width: 8.33%;}
  .col-md-2 {width: 16.66%;}
  .col-md-3 {width: 25%;}
  .col-md-4 {width: 33.33%;}
  .col-md-5 {width: 41.66%;}
  .col-md-6 {width: 50%;}
  .col-md-7 {width: 58.33%;}
  .col-md-8 {width: 66.66%;}
  .col-md-9 {width: 75%;}
  .col-md-10 {width: 83.33%;}
  .col-md-11 {width: 91.66%;}
  .col-md-12 {width: 100%;}

  .website-container {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .toggler {
    position: absolute;
    top: 1rem;
    right: 1rem;
  }

  .navbar-collapse {
    /* background: #262b55; */
    max-height: 0px;
    flex-direction: column;
    width: 100%;
    transition: all 1s ease-in-out;
  }

  .navbar {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .navbar-nav {
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
  }

  .navbar-nav ul {
    width: 100%;
    margin: 0px;
    padding: 0px;
  }

  .navbar-nav li {
    width: 100%;
    text-align: right;
    padding: .5rem 0rem;
  }

  .language {
    width: 100%;
    padding: .5rem 0rem;
  }

  .responsive {
    display: flex;
    height: auto;
    max-height: 100vh;
    transition: all 1s ease-in-out;
  }

  .main-nav {
    background: linear-gradient(90deg, #d0e3f3 0%, #262b55 40%, #262b55 100%);
  }

  .image-landscape,
  .image-portrait {
    width: 45%;
    max-width: 45%;
  }

  .image-landscape {
    max-height: 200px;
  }

  .image-portrait {
    max-height: 500px;
  }

  .sub-menu.show {
    display: flex !important;
  }

  ul.sub-menu {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    position: absolute;
    padding-right: 20px;
    margin-top: 0px;
    margin-bottom: 0px;
    padding-top: 0px;
    padding-bottom: 0px;
    z-index: 100;
    border-radius: .5rem;
    background-color: #000;
    min-height: 50px;
    max-height: 900px;
    height: auto;
    overflow: visible;
  }
}

/* laptops/desktops >= 992px */
@media only screen and (min-width: 992px) {
  .col-1 {width: 8.33%;}
  .col-2 {width: 16.66%;}
  .col-3 {width: 25%;}
  .col-4 {width: 33.33%;}
  .col-5 {width: 41.66%;}
  .col-6 {width: 50%;}
  .col-7 {width: 58.33%;}
  .col-8 {width: 66.66%;}
  .col-9 {width: 75%;}
  .col-10 {width: 83.33%;}
  .col-11 {width: 91.66%;}
  .col-12 {width: 100%;}

  .website-container {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .toggler {
    display: none;
  }

  .navbar {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
  }

  .navbar-nav {
    flex-direction: row;
    justify-content: space-around;
    align-items: flex-end;
  }

  .navbar-collapse {
    background-color: transparent;
    max-height: unset;
    flex-direction: row;
    width: auto;
  }

  .navbar-collapse .main-nav-items {
    flex-basis: auto;
    display: flex;
    flex-grow: 1;
    text-align: right;
  }

  .language {
    flex-basis: auto;
    display: flex;
    width: auto;
    padding-right: 20px;
    padding-left: 20px;
  }

  .navbar-nav ul {
    width: auto;
    margin: 0px;
    padding: 0px;
  }

  .navbar-nav li {
    width: auto;
    text-align: left;
    padding: .5rem .5rem;
    margin: 0px;
  }

  .main-nav {
    background: linear-gradient(90deg, #d0e3f3 0%, #262b55 25%, #262b55 100%);
  }

  .image-landscape,
  .image-portrait {
    width: 45%;
    max-width: 45%;
  }

  .image-landscape {
    max-height: 200px;
  }

  .image-portrait {
    max-height: 500px;
  }
}

/* large laptops and desktops >= 1200px */
@media only screen and (min-width: 1200px) {
  .col-xl-1 {width: 8.33%;}
  .col-xl-2 {width: 16.66%;}
  .col-xl-3 {width: 25%;}
  .col-xl-4 {width: 33.33%;}
  .col-xl-5 {width: 41.66%;}
  .col-xl-6 {width: 50%;}
  .col-xl-7 {width: 58.33%;}
  .col-xl-8 {width: 66.66%;}
  .col-xl-9 {width: 75%;}
  .col-xl-10 {width: 83.33%;}
  .col-xl-11 {width: 91.66%;}
  .col-xl-12 {width: 100%;}

  .website-container {
    max-width: 1366px;
    margin-left: auto;
    margin-right: auto;
  }

  .toggler {
    display: none;
  }

  .navbar-brand {
    padding-right: 120px;
  }

  .navbar {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
  }

  .navbar-nav {
    flex-direction: row;
    justify-content: space-between;
  }

  .navbar-collapse {
    background-color: transparent;
    max-height: unset;
    flex-direction: row;
    width: auto;
  }

  .navbar-collapse .main-nav-items {
    flex-basis: auto;
    display: flex;
    flex-grow: 1;
    text-align: right;
  }

  .language {
    flex-basis: auto;
    display: flex;
    width: auto;
    padding-right: 40px;
    padding-left: 20px;
  }

  .navbar-nav ul {
    width: auto;
    margin: 0px;
    padding: 0px;
  }

  .navbar-nav li {
    width: auto;
    text-align: left;
    padding: .5rem .5rem;
  }

  .main-nav {
    background: linear-gradient(90deg, #d0e3f3 0%, #262b55 25%, #262b55 100%);
  }

  .image-landscape,
  .image-portrait {
    width: 32%;
    max-width: 32%;
  }

  .image-landscape {
    max-height: 240px;
  }
}
</style>
